import { CircleCheck, LoaderCircle, RefreshCw, TriangleAlert } from 'lucide-react'
import { toast } from 'sonner'
import { Note, Track } from '@/components/redesign/bits'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { cn } from '@/lib/cn'
import { formatRelative } from '@/lib/format'
import { type LimitStatus, type ServiceLimit, useRefreshLimits, useServiceLimits } from './api'
import { CspReportsCard } from './csp-reports-card'
import { SecurityFeedCard } from './security-feed-card'
import { LearnedLinesCard, ScriptHostsCard } from './security-learning-cards'
import { DataSpendPanels } from './data-spend'

const STATUS: Record<LimitStatus, { label: string; tone: string }> = {
  ok: { label: 'OK', tone: 'ok' },
  warning: { label: 'Getting close', tone: 'warn' },
  critical: { label: 'Nearly at the limit', tone: 'bad' },
  not_connected: { label: 'Not connected', tone: '' },
  unavailable: { label: 'Couldn’t check', tone: '' },
}

/**
 * Owners: the providers Arva SEO depends on and how close each is to its limit, as meters
 * (redesign of 2026-10-16), what paid data and AI credits cost by day, then what browsers report
 * the security policy blocks.
 */
export function LimitsPage() {
  const limits = useServiceLimits()
  const refresh = useRefreshLimits()
  const attention =
    limits.data?.data.filter((i) => i.status === 'warning' || i.status === 'critical').length ?? 0

  return (
    <>
      <PageTitle
        title="Service limits"
        description="How close Arva SEO is to the limits of the data providers and services it runs on. Owners get an email each morning about anything at 80% or more. Only owners see this page."
        actions={
          <button
            type="button"
            className="btn"
            disabled={refresh.isPending}
            aria-busy={refresh.isPending}
            onClick={() =>
              refresh.mutate(undefined, {
                onSuccess: () => toast.success('Checked just now.'),
                onError: () => toast.error('The check didn’t finish. Try again in a moment.'),
              })
            }
          >
            {refresh.isPending ? (
              <LoaderCircle aria-hidden="true" className="animate-spin" />
            ) : (
              <RefreshCw aria-hidden="true" />
            )}
            {refresh.isPending ? 'Checking…' : 'Check now'}
          </button>
        }
      />
      {limits.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : limits.isError ? (
        <div className="card">
          <ErrorState error={limits.error} onRetry={() => void limits.refetch()} />
        </div>
      ) : (
        <>
          <Note
            calm={attention === 0}
            icon={attention === 0 ? <CircleCheck aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />}
            title={
              attention === 0
                ? 'Everything is within its limits.'
                : `${attention} need${attention === 1 ? 's' : ''} attention.`
            }
          >
            Checked {formatRelative(limits.data.meta.checked_at)}; the server checks again every 10 minutes.
          </Note>
          <ul
            className={cn('usage transition-opacity', refresh.isPending && 'opacity-50')}
            aria-label="Service limits"
            aria-busy={refresh.isPending}
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {limits.data.data.map((item) => (
              <Meter key={item.key} item={item} />
            ))}
          </ul>
        </>
      )}
      <DataSpendPanels />
      <CspReportsCard />
      <SecurityFeedCard />
      <LearnedLinesCard />
      <ScriptHostsCard />
    </>
  )
}

function Meter({ item }: { item: ServiceLimit }) {
  const s = STATUS[item.status]
  const ratio = item.percent !== null ? item.percent / 100 : null
  return (
    <li className="use">
      <div className="use-top">
        <span>
          {item.label}
          <small style={{ display: 'block' }}>{item.service}</small>
        </span>
        {item.status !== 'ok' && <span className={cn('chip', s.tone)}>{s.label}</span>}
      </div>
      <b style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14 }}>{item.value}</b>
      {ratio !== null && (
        <span
          role="meter"
          aria-label={`${item.label}: ${item.percent}% used`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={item.percent ?? 0}
        >
          <Track
            ratio={Math.max(ratio, 0.02)}
            tone={item.status === 'critical' ? 'bad' : item.status === 'warning' ? 'warn' : ''}
          />
        </span>
      )}
      {(item.detail || ratio !== null) && (
        <small className={cn((item.status === 'warning' || item.status === 'critical') && 'warn')}>
          {ratio !== null && item.status !== 'ok' && `${item.percent}% used`}
          {ratio !== null && item.status !== 'ok' && item.detail && ' · '}
          {item.detail}
        </small>
      )}
      {item.how_to && (
        <small style={{ background: 'var(--sunken)', borderRadius: 8, padding: '6px 8px' }}>
          {item.how_to}
        </small>
      )}
    </li>
  )
}
